import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";

import {
  getAssignmentFileForms,
  saveAssignmentFileForms,
  type AssignmentFileFormsApplicationItem,
} from "../../api/assignment";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { parseFilingApiDateString, toLocalYmd } from "../../utils/filingApiDates";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const ASSIGNMENT_FILE_FORMS_FORM_ID = "assignment-file-forms-form";

const ACCEPTED_TYPES = ".pdf";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const UPLOAD_FOLDER_PREFIX = "assignment/filing-forms";

const HEADER_CELL =
  "text-[12px] font-normal leading-5 text-[#6D6D6D]";
const REQ = <span className="text-red-500 ml-0.5">*</span>;

type ApplicationRowState = AssignmentFileFormsApplicationItem & {
  pendingFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
};

type AssignmentFileFormsViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function dateValue(value: string | null | undefined): Date | null {
  return parseFilingApiDateString(value ?? null);
}

function toIsoDate(value: string | null | undefined): string | undefined {
  const d = parseFilingApiDateString(value ?? null);
  return d ? toLocalYmd(d) : undefined;
}

export default function AssignmentFileFormsView({
  leadId,
  onSuccess,
}: AssignmentFileFormsViewProps) {
  const [applications, setApplications] = useState<ApplicationRowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [uploadingIds, setUploadingIds] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [dateErrors, setDateErrors] = useState<Record<string, boolean>>({});

  const autoSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setApplications([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAssignmentFileForms(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load File Forms data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
      autoSaveTimers.current = {};
    };
  }, []);

  const totalCount = applications.length;
  const filedCount = useMemo(
    () =>
      applications.filter(
        (app) => (app.dateOfFilingAssignment ?? "").trim().length > 0
      ).length,
    [applications]
  );

  const scheduleDateAutoSave = (
    applicationId: string,
    isoDate: string | null
  ) => {
    if (!leadId) return;
    const key = `date-${applicationId}`;
    const existing = autoSaveTimers.current[key];
    if (existing) clearTimeout(existing);
    autoSaveTimers.current[key] = setTimeout(() => {
      saveAssignmentFileForms(leadId, {
        applications: [
          {
            applicationId,
            dateOfFilingAssignment: isoDate,
          },
        ],
      })
        .then((data) => {
          setApplications((prev) =>
            mergeApplications(prev, data.applications)
          );
        })
        .catch(() => {
          // Auto-save errors are non-blocking; final submit will surface real issues.
        });
    }, 600);
  };

  const handleDateChange = (index: number, next: Date | null) => {
    setApplications((prev) => {
      const arr = [...prev];
      const row = arr[index];
      if (!row) return prev;
      const iso = next ? toLocalYmd(next) : null;
      arr[index] = { ...row, dateOfFilingAssignment: iso };
      scheduleDateAutoSave(row.applicationId, iso);
      if (iso) {
        setDateErrors((p) => {
          if (!p[row.applicationId]) return p;
          const n = { ...p };
          delete n[row.applicationId];
          return n;
        });
      }
      return arr;
    });
  };

  const setRowUploading = (applicationId: string, value: boolean) => {
    setUploadingIds((prev) => {
      if (value) return { ...prev, [applicationId]: true };
      const next = { ...prev };
      delete next[applicationId];
      return next;
    });
  };

  const uploadFileForRow = async (index: number, file: File) => {
    if (!leadId) return;
    const row = applications[index];
    if (!row) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    const applicationId = row.applicationId;
    setApplications((prev) => {
      const arr = [...prev];
      const target = arr.findIndex((a) => a.applicationId === applicationId);
      if (target === -1) return prev;
      arr[target] = { ...arr[target], pendingFilename: file.name };
      return arr;
    });
    setRowUploading(applicationId, true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [
          {
            filename: file.name,
            mimeType: file.type || "application/pdf",
          },
        ],
        "private",
        `${UPLOAD_FOLDER_PREFIX}/${leadId}`
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/pdf",
        },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const data = await saveAssignmentFileForms(leadId, {
        applications: [
          {
            applicationId,
            assignmentFormPdfFileKey: presigned.key,
            assignmentFormPdfFilename: file.name,
          },
        ],
      });

      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setApplications((prev) =>
        prev.map((app) =>
          app.applicationId === applicationId
            ? { ...app, pendingFilename: null }
            : app
        )
      );
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setRowUploading(applicationId, false);
    }
  };

  const removeUploadedPdf = async (index: number) => {
    if (!leadId) return;
    const row = applications[index];
    if (!row?.assignmentFormPdfFileKey) return;
    try {
      const data = await saveAssignmentFileForms(leadId, {
        applications: [
          {
            applicationId: row.applicationId,
            assignmentFormPdfFileKey: null,
            assignmentFormPdfFilename: null,
          },
        ],
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File removed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove file");
    }
  };

  const openPreview = (row: ApplicationRowState) => {
    if (!row.pdfDownloadUrl || !row.assignmentFormPdfFilename) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreview({
      url: row.pdfDownloadUrl,
      name: row.assignmentFormPdfFilename,
      kind: detectPreviewKind(row.assignmentFormPdfFilename),
    });
  };

  const closePreview = () => setPreview(null);

  const handleDownload = async (row: ApplicationRowState) => {
    if (!row.pdfDownloadUrl || !row.assignmentFormPdfFilename) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(
        row.pdfDownloadUrl,
        row.assignmentFormPdfFilename
      );
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error("No applications found. Complete the previous steps first.");
      return;
    }
    const nextErrors: Record<string, boolean> = {};
    applications.forEach((app) => {
      if (!toIsoDate(app.dateOfFilingAssignment)) {
        nextErrors[app.applicationId] = true;
      }
    });
    if (Object.keys(nextErrors).length > 0) {
      setDateErrors(nextErrors);
      return;
    }
    setDateErrors({});
    const hasAtLeastOneUpload = applications.some(
      (app) => !!app.assignmentFormPdfFileKey
    );
    if (!hasAtLeastOneUpload) {
      toast.error("Please upload the filed form for at least one application.");
      return;
    }
    Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
    autoSaveTimers.current = {};
    setSaving(true);
    try {
      const data = await saveAssignmentFileForms(leadId, {
        applications: applications.map((app) => ({
          applicationId: app.applicationId,
          dateOfFilingAssignment: toIsoDate(app.dateOfFilingAssignment) ?? null,
        })),
      });
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File Forms for Assignment saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading File Forms data...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found. Please complete the Invoice step first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={ASSIGNMENT_FILE_FORMS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {applications.length > 1 && (
        <p
          className="text-xs font-medium leading-5 text-[#222]"
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        >
          {filedCount} of {totalCount} application{totalCount === 1 ? "" : "s"}{" "}
          ready to file.
        </p>
      )}

      <div className="self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-3 md:p-4 overflow-x-auto">
        <div className="min-w-[640px] flex flex-col">
          <div className="grid grid-cols-[160px_180px_minmax(280px,1fr)] gap-3 px-1 pb-2">
            <div className={HEADER_CELL}>TM No.</div>
            <div className={HEADER_CELL}>Date of Filing Assignment{REQ}</div>
            <div className={HEADER_CELL}>Upload File</div>
          </div>

          {applications.map((row, idx) => {
            const inputId = `assignment-file-forms-input-${row.applicationId}`;
            const uploading = !!uploadingIds[row.applicationId];
            const fileLabel =
              row.pendingFilename ?? row.assignmentFormPdfFilename ?? null;
            const hasFile = !!fileLabel;
            const canPreview = !!row.pdfDownloadUrl && !!row.assignmentFormPdfFilename;

            return (
              <div
                key={row.applicationId}
                className="flex flex-col gap-2 py-3 border-t border-[#EEEEEE] first:border-t-0"
              >
                <div className="grid grid-cols-[160px_180px_minmax(280px,1fr)] gap-3 px-1 items-center">
                  <div
                    className="text-[12px] leading-5 text-[#222222] truncate"
                    title={row.tmNo || ""}
                  >
                    {row.tmNo || "—"}
                  </div>
                  <DateTimePicker
                    value={dateValue(row.dateOfFilingAssignment)}
                    onChange={(d) => handleDateChange(idx, d)}
                    showTime={false}
                    placeholder="Select Date"
                    buttonClassName={`px-[10px] py-2 rounded-[6px] text-[12px] ${
                      dateErrors[row.applicationId]
                        ? "!border-red-400 !ring-1 !ring-red-400"
                        : ""
                    }`}
                  />
                  {hasFile ? (
                    <div className="flex items-center justify-between gap-2 h-9 px-3 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
                      <span
                        className="text-[12px] text-[#16A34A] truncate min-w-0 flex-1"
                        title={fileLabel ?? ""}
                      >
                        {uploading ? "Uploading…" : fileLabel}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {canPreview && (
                          <button
                            type="button"
                            onClick={() => openPreview(row)}
                            className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Preview
                          </button>
                        )}
                        {canPreview && (
                          <button
                            type="button"
                            onClick={() => void handleDownload(row)}
                            className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50"
                          >
                            Download
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={uploading}
                          onClick={() =>
                            document.getElementById(inputId)?.click()
                          }
                          className="px-2 h-7 rounded-[6px] border border-[#D5D3E0] bg-white text-[11px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          aria-label={`Remove ${fileLabel}`}
                          onClick={() => void removeUploadedPdf(idx)}
                          disabled={uploading}
                          className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                          title="Remove"
                        >
                          <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() =>
                        document.getElementById(inputId)?.click()
                      }
                      className="h-9 w-full flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{
                        boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "14px",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {uploading ? "Uploading…" : "Select File"}
                    </button>
                  )}
                </div>

                <input
                  id={inputId}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void uploadFileForRow(idx, file);
                    e.currentTarget.value = "";
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function mergeApplications(
  prev: ApplicationRowState[],
  next: AssignmentFileFormsApplicationItem[]
): ApplicationRowState[] {
  const prevById = new Map(prev.map((a) => [a.applicationId, a]));
  return next.map((app) => {
    const cached = prevById.get(app.applicationId);
    return {
      ...app,
      pendingFilename: cached?.pendingFilename ?? null,
    };
  });
}
